import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import Swal from 'sweetalert2';
import { buscarEstatisticas, buscarEstatisticasMensais, buscarEstatisticasNaoAtendidos, buscarEstatisticasNaoAtendidosMensais } from '../../service/UserService';

const COLORS = ["#1e40af", "#dc2626", "#16a34a", "#f59e0b", "#7c3aed", "#0891b2"];

function Dashboard() {
  const navigate = useNavigate();
  const [estatisticas, setEstatisticas] = useState(null);
  const [estatisticasNaoAtendidos, setEstatisticasNaoAtendidos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtros, setFiltros] = useState({
    ano: new Date().getFullYear(),
    mes: new Date().getMonth() + 1,
    tipoVisualizacao: 'mensal' 
  });
  const [categoriaModal, setCategoriaModal] = useState(null);

  useEffect(() => {
    const carregarEstatisticas = async () => {
      try {
        setLoading(true);
        let dados, dadosNaoAtendidos = null;
        
        if (filtros.tipoVisualizacao === 'mensal') {
          dados = await buscarEstatisticasMensais(filtros.ano, filtros.mes);
          
          try {
            dadosNaoAtendidos = await buscarEstatisticasNaoAtendidosMensais(filtros.ano, filtros.mes);
          } catch (err) {
            console.warn('Erro ao carregar estatísticas de não atendidos mensais:', err.message);
          }
        } else {
          dados = await buscarEstatisticas();
          
          try {
            dadosNaoAtendidos = await buscarEstatisticasNaoAtendidos();
          } catch (err) {
            console.warn('Erro ao carregar estatísticas de não atendidos gerais:', err.message);
          }
        }
        
        setEstatisticas(dados);
        setEstatisticasNaoAtendidos(dadosNaoAtendidos);
        setError(null);
      } catch (err) {
        setError(err.message);
        Swal.fire({
          icon: 'error',
          title: 'Erro ao carregar dados',
          text: 'Não foi possível carregar as estatísticas do dashboard.',
          confirmButtonText: 'OK',
          confirmButtonColor: '#ef4444'
        });
      } finally {
        setLoading(false);
      }
    };

    carregarEstatisticas();
  }, [filtros]);

  const formatarTempo = (minutos) => {
    if (!minutos || minutos === 0) return '0min';
    
    const horas = Math.floor(minutos / 60);
    const mins = Math.round(minutos % 60);
    
    if (horas === 0) return `${mins}min`;
    if (mins === 0) return `${horas}h`;
    return `${horas}h ${mins}min`;
  };

  const LegendaCustomizada = ({ payload, maxItems = 10 }) => {
    if (!payload || payload.length === 0) return null;
    
    const sortedPayload = [...payload].sort((a, b) => b.value - a.value);
    const itemsToShow = sortedPayload.slice(0, maxItems);
    const hasMore = sortedPayload.length > maxItems;
    
    return (
      <div style={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        justifyContent: 'center', 
        gap: '8px', 
        fontSize: '10px',
        padding: '10px 0'
      }}>
        {itemsToShow.map((entry, index) => (
          <div key={index} style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '4px',
            maxWidth: '120px'
          }}>
            <div style={{
              width: '12px',
              height: '12px',
              backgroundColor: entry.color,
              borderRadius: '2px',
              flexShrink: 0
            }} />
            <span style={{ 
              color: '#374151',
              fontSize: '9px',
              lineHeight: '1.2',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {entry.value}
            </span>
          </div>
        ))}
        {hasMore && (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center',
            color: '#6b7280',
            fontSize: '10px',
            fontWeight: '500'
          }}>
            ...
          </div>
        )}
      </div>
    );
  };

  const categorizarDados = (tiposSinistro) => {
    if (!tiposSinistro || tiposSinistro.length === 0) return {
      sinistrosTransito: [],
      transitoSeguro: [],
      estacionamentoProibido: [],
      resumo: []
    };

    const sinistrosTransito = [];
    const transitoSeguro = [];
    const estacionamentoProibido = [];

    tiposSinistro.forEach(item => {
      const natureza = item.natureza?.toLowerCase() || '';
      
      // Categorias específicas de sinistros de trânsito
      if (natureza.includes('sinistro de trânsito com vítima') || 
          natureza.includes('sinistro de trânsito sem vítima') ||
          natureza.includes('sinistro de transito com vitima') || 
          natureza.includes('sinistro de transito sem vitima') ||
          natureza.includes('acidente') ||
          natureza.includes('atropelamento') || 
          natureza.includes('capotamento') || 
          natureza.includes('queda de moto') || 
          natureza.includes('colisão') ||
          natureza.includes('colisao')) {
        sinistrosTransito.push(item);
      } else if (natureza.includes('estacionamento') || natureza.includes('estacionar') || 
                 natureza.includes('parar') || natureza.includes('vaga') || natureza.includes('proibido')) {
        estacionamentoProibido.push(item);
      } else {
        // Todos os demais tipos vão para trânsito seguro
        transitoSeguro.push(item);
      }
    });

    const recalcularPercentuais = (dados) => {
      const totalGrupo = dados.reduce((sum, item) => sum + item.quantidade, 0);
      return dados.map(item => ({
        ...item,
        percentual: totalGrupo > 0 ? ((item.quantidade / totalGrupo) * 100).toFixed(1) : 0
      })).filter(item => item.quantidade > 0 && parseFloat(item.percentual) >= 0.1);
    };

    const limitarDados = (dados) => dados;

    const totalSinistrosTransito = sinistrosTransito.reduce((sum, item) => sum + item.quantidade, 0);
    const totalTransitoSeguro = transitoSeguro.reduce((sum, item) => sum + item.quantidade, 0);
    const totalEstacionamento = estacionamentoProibido.reduce((sum, item) => sum + item.quantidade, 0);
    const total = totalSinistrosTransito + totalTransitoSeguro + totalEstacionamento;

    const resumo = [
      {
        natureza: 'Sinistros de Trânsito',
        quantidade: totalSinistrosTransito,
        percentual: total > 0 ? ((totalSinistrosTransito / total) * 100).toFixed(1) : 0,
        key: 'sinistrosTransito',
        icon: '🚗',
        color: COLORS[0]
      },
      {
        natureza: 'Trânsito Seguro',
        quantidade: totalTransitoSeguro,
        percentual: total > 0 ? ((totalTransitoSeguro / total) * 100).toFixed(1) : 0,
        key: 'transitoSeguro',
        icon: '🛡️',
        color: COLORS[1]
      },
      {
        natureza: 'Estacionamento Proibido',
        quantidade: totalEstacionamento,
        percentual: total > 0 ? ((totalEstacionamento / total) * 100).toFixed(1) : 0,
        key: 'estacionamentoProibido',
        icon: '🚫',
        color: COLORS[2]
      }
    ].filter(item => item.quantidade > 0);

    return {
      sinistrosTransito: recalcularPercentuais(limitarDados(sinistrosTransito)),
      transitoSeguro: recalcularPercentuais(limitarDados(transitoSeguro)),
      estacionamentoProibido: recalcularPercentuais(limitarDados(estacionamentoProibido)),
      resumo
    };
  };

  const handleCategoriaClick = (categoria) => {
    if (!categoria || !dadosProcessados) return;
    
    const categoriaKey = categoria.key;
    const dadosCategoria = dadosProcessados.dadosCategorizados[categoriaKey];
    
    if (dadosCategoria && dadosCategoria.length > 0) {
      setCategoriaModal({
        titulo: categoria.natureza,
        icon: categoria.icon,
        color: categoria.color,
        dados: dadosCategoria,
        total: categoria.quantidade
      });
    }
  };

  const handleTipoNaoAtendidoClick = (tipoSinistro) => {
    const filtrosNavegacao = {
      apenasNaoAtendidas: true,
      tipoNaoAtendida: tipoSinistro.natureza
    };

    // Se estiver na visualização mensal, adiciona as datas
    if (filtros.tipoVisualizacao === 'mensal') {
      const ano = filtros.ano;
      const mes = filtros.mes;
      
      // Primeiro dia do mês
      const dataInicial = `01/${mes.toString().padStart(2, '0')}/${ano}`;
      
      // Último dia do mês
      const ultimoDia = new Date(ano, mes, 0).getDate();
      const dataFinal = `${ultimoDia}/${mes.toString().padStart(2, '0')}/${ano}`;
      
      filtrosNavegacao.dataInicial = dataInicial;
      filtrosNavegacao.dataFinal = dataFinal;
    }

    // Navegar para a tela de relatório com os filtros como parâmetros de URL
    const searchParams = new URLSearchParams();
    Object.entries(filtrosNavegacao).forEach(([key, value]) => {
      searchParams.append(key, value);
    });

    navigate(`/relatorio?${searchParams.toString()}`);
  };

  const dadosProcessados = estatisticas ? {
    isMensal: filtros.tipoVisualizacao === 'mensal',
    totalOcorrencias: estatisticas.totalOcorrencias || 0,
    tempoMedioDeslocamento: filtros.tipoVisualizacao === 'mensal' 
      ? formatarTempo(estatisticas.tempoMedioDeslocamentoChegada)
      : (estatisticas.tempoMedioDeslocamento?.formatted || '0h 0min'),
      
    tempoMedioAtendimento: filtros.tipoVisualizacao === 'mensal'
      ? formatarTempo(estatisticas.tempoMedioDespachoChegada)
      : (estatisticas.tempoMedioAtendimento?.formatted || '0h 0min'),
      
    tempoMedioDespachoLiberacao: filtros.tipoVisualizacao === 'mensal'
      ? formatarTempo(estatisticas.tempoMedioDespachoLiberacao)
      : (estatisticas.tempoMedioDespachoLiberacao?.formatted || '0h 0min'),
      
    totalEmpenhosAnalisados: estatisticas.totalEmpenhosAnalisados || 0,

    tiposSinistro: estatisticas.tiposSinistro?.map(item => ({
      natureza: item.natureza?.replace(/\s+/g, ' ').trim() || 'Não informado',
      quantidade: item.quantidade,
      percentual: item.percentual
    })) || [],

    dadosCategorizados: categorizarDados(estatisticas.tiposSinistro),

    dadosDeslocamento: [
      { 
        categoria: 'Tempo Médio de Deslocamento', 
        tempo: filtros.tipoVisualizacao === 'mensal' 
          ? Math.round(estatisticas.tempoMedioDeslocamentoChegada || 0)
          : (estatisticas.tempoMedioDeslocamento?.horas || 0) * 60 + (estatisticas.tempoMedioDeslocamento?.minutos || 0),
        formatted: filtros.tipoVisualizacao === 'mensal'
          ? formatarTempo(estatisticas.tempoMedioDeslocamentoChegada)
          : (estatisticas.tempoMedioDeslocamento?.formatted || '0h 0min')
      }
    ],
    
    dadosAtendimento: [
      { 
        categoria: 'Tempo Médio de Atendimento', 
        tempo: filtros.tipoVisualizacao === 'mensal'
          ? Math.round(estatisticas.tempoMedioDespachoChegada || 0)
          : (estatisticas.tempoMedioAtendimento?.horas || 0) * 60 + (estatisticas.tempoMedioAtendimento?.minutos || 0),
        formatted: filtros.tipoVisualizacao === 'mensal'
          ? formatarTempo(estatisticas.tempoMedioDespachoChegada)
          : (estatisticas.tempoMedioAtendimento?.formatted || '0h 0min')
      }
    ]
  } : null;

  return (
    <>
      <style>{`
        .dashboard {
          padding: 2rem;
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
          min-height: 100vh;
          font-family: 'Inter', system-ui, sans-serif;
        }

        .dashboard-header {
          margin-bottom: 2rem;
        }

        .dashboard-title {
          font-size: 2.5rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 0.5rem;
          background: linear-gradient(135deg, #1e40af, #3b82f6);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
        }

        .dashboard-subtitle {
          font-size: 1.1rem;
          color: #64748b;
          font-weight: 500;
        }

        .filters-section {
          background: white;
          padding: 1.5rem;
          border-radius: 16px;
          margin-bottom: 2rem;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          border: 1px solid rgba(226, 232, 240, 0.8);
        }

        .filters-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
        }

        .filter-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .filter-label {
          font-size: 0.875rem;
          font-weight: 600;
          color: #374151;
        }

        .filter-select {
          padding: 0.75rem 1rem;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 0.875rem;
          transition: all 0.2s ease;
          background: white;
        }

        .filter-select:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .dashboard-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
          gap: 2rem;
          margin-bottom: 2rem;
        }

        .card {
          background: white;
          padding: 1.5rem;
          border-radius: 16px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          border: 1px solid rgba(226, 232, 240, 0.8);
          transition: all 0.3s ease;
        }

        .card:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 25px -3px rgba(0, 0, 0, 0.1);
        }

        .card-full {
          grid-column: 1 / -1;
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 2rem;
          align-items: center;
        }

        .card-clickable {
          cursor: pointer;
          position: relative;
        }

        .card-clickable::after {
          content: '👆 Clique para ver detalhes';
          position: absolute;
          top: 1rem;
          right: 1rem;
          background: #3b82f6;
          color: white;
          padding: 0.5rem 1rem;
          border-radius: 8px;
          font-size: 0.75rem;
          font-weight: 600;
          opacity: 0;
          transition: opacity 0.3s ease;
          pointer-events: none;
        }

        .card-clickable:hover::after {
          opacity: 1;
        }

        .card h2 {
          font-size: 1.25rem;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 1rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .card h3 {
          font-size: 1.1rem;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 0.5rem;
        }

        .summary-stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 1.5rem;
        }

        .stat-item {
          text-align: center;
          padding: 1rem;
          background: linear-gradient(135deg, #f8fafc, #e2e8f0);
          border-radius: 12px;
          border: 1px solid #e2e8f0;
        }

        .stat-number {
          font-size: 2.5rem;
          font-weight: 700;
          color: #1e40af;
          margin-bottom: 0.25rem;
          line-height: 1;
        }

        .stat-label {
          font-size: 0.875rem;
          color: #64748b;
          font-weight: 500;
        }

        .highlight {
          color: #1e40af;
          font-weight: 700;
          font-size: 1.5rem;
        }

        .chart-container {
          height: 300px;
          margin-top: 1rem;
        }

        .chart-container-pie {
          height: 400px;
          margin-top: 1rem;
        }

        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 2rem;
          animation: fadeIn 0.3s ease;
          backdrop-filter: blur(4px);
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .modal-content {
          background: white;
          border-radius: 24px;
          padding: 2rem;
          max-width: 900px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          animation: slideUp 0.3s ease;
          position: relative;
        }

        @keyframes slideUp {
          from {
            transform: translateY(50px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          padding-bottom: 1rem;
          border-bottom: 2px solid #f1f5f9;
        }

        .modal-title {
          display: flex;
          align-items: center;
          gap: 1rem;
          font-size: 1.75rem;
          font-weight: 700;
          color: #1e293b;
        }

        .modal-icon {
          font-size: 2.5rem;
        }

        .modal-close {
          background: #f1f5f9;
          border: none;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 1.5rem;
          color: #64748b;
        }

        .modal-close:hover {
          background: #dc2626;
          color: white;
          transform: rotate(90deg);
        }

        .modal-stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 1rem;
          margin-bottom: 2rem;
        }

        .modal-stat-card {
          background: linear-gradient(135deg, #f8fafc, #e2e8f0);
          padding: 1.5rem;
          border-radius: 12px;
          text-align: center;
          border: 1px solid #e2e8f0;
        }

        .modal-stat-number {
          font-size: 2rem;
          font-weight: 700;
          color: #1e40af;
          margin-bottom: 0.25rem;
        }

        .modal-stat-label {
          font-size: 0.875rem;
          color: #64748b;
          font-weight: 500;
        }

        .modal-chart {
          margin-bottom: 2rem;
        }

        .modal-list {
          background: #f8fafc;
          border-radius: 12px;
          padding: 1rem;
          max-height: 400px;
          overflow-y: auto;
        }

        .modal-list-title {
          font-size: 1.1rem;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 1rem;
          padding: 0 0.5rem;
        }

        .modal-list-item {
          background: white;
          padding: 1rem;
          margin-bottom: 0.75rem;
          border-radius: 8px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          transition: all 0.2s ease;
          border-left: 3px solid transparent;
        }

        .modal-list-item:hover {
          transform: translateX(4px);
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }

        .modal-list-item-name {
          font-weight: 600;
          color: #1e293b;
          flex: 1;
        }

        .modal-list-item-stats {
          display: flex;
          gap: 1rem;
          align-items: center;
        }

        .modal-list-item-count {
          background: #eff6ff;
          color: #1e40af;
          padding: 0.25rem 0.75rem;
          border-radius: 6px;
          font-weight: 600;
          font-size: 0.875rem;
        }

        .modal-list-item-percent {
          color: #64748b;
          font-weight: 600;
          font-size: 0.875rem;
        }

        .recent-list {
          max-height: 300px;
          overflow-y: auto;
        }

        .recent-item {
          padding: 1rem;
          border-bottom: 1px solid #f1f5f9;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 1rem;
        }

        .recent-item:last-child {
          border-bottom: none;
        }

        .recent-info {
          flex: 1;
        }

        .recent-numero {
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 0.25rem;
        }

        .recent-natureza {
          font-size: 0.875rem;
          color: #3b82f6;
          margin-bottom: 0.25rem;
        }

        .recent-local {
          font-size: 0.75rem;
          color: #64748b;
        }

        .status-badge {
          padding: 0.25rem 0.75rem;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 600;
          color: white;
        }

        .status-em-andamento {
          background: #f59e0b;
        }

        .status-concluido {
          background: #10b981;
        }

        .prioridade-alta {
          background: #dc2626;
        }

        .prioridade-media {
          background: #f59e0b;
        }

        .prioridade-baixa {
          background: #10b981;
        }

        @media (max-width: 768px) {
          .dashboard {
            padding: 1rem;
          }

          .dashboard-grid {
            grid-template-columns: 1fr !important;
          }

          .card-full {
            grid-template-columns: 1fr;
          }

          .summary-stats {
            grid-template-columns: repeat(2, 1fr);
          }

          .modal-overlay {
            padding: 1rem;
          }

          .modal-content {
            padding: 1.5rem;
          }

          .modal-title {
            font-size: 1.25rem;
          }

          .modal-stats {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <div className="dashboard">
        {/* Header */}
        <div className="dashboard-header">
          <h1 className="dashboard-title">Dashboard de Ocorrências</h1>
          <p className="dashboard-subtitle">
            {filtros.tipoVisualizacao === 'mensal' 
              ? `Análise mensal - ${['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
                  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'][filtros.mes - 1]} de ${filtros.ano}`
              : 'Análise dos dados extraídos dos PDFs processados'
            }
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '200px',
            fontSize: '1.2rem',
            color: '#64748b'
          }}>
            Carregando estatísticas...
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div style={{ 
            textAlign: 'center', 
            padding: '2rem',
            color: '#dc2626',
            fontSize: '1.1rem'
          }}>
            Erro ao carregar dados: {error}
          </div>
        )}

        {!loading && !error && dadosProcessados && (
          <>
            <div className="filters-section">
              <div className="filters-grid">
                <div className="filter-group">
                  <label className="filter-label">Tipo de Visualização</label>
                  <select 
                    className="filter-select"
                    value={filtros.tipoVisualizacao}
                    onChange={(e) => setFiltros(prev => ({...prev, tipoVisualizacao: e.target.value}))}
                  >
                    <option value="geral">Dados Gerais</option>
                    <option value="mensal">Por Mês</option>
                  </select>
                </div>
                
                {filtros.tipoVisualizacao === 'mensal' && (
                  <>
                    <div className="filter-group">
                      <label className="filter-label">Ano</label>
                      <select 
                        className="filter-select"
                        value={filtros.ano}
                        onChange={(e) => setFiltros(prev => ({...prev, ano: parseInt(e.target.value)}))}
                      >
                        {[2024, 2025, 2026].map(ano => (
                          <option key={ano} value={ano}>{ano}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="filter-group">
                      <label className="filter-label">Mês</label>
                      <select 
                        className="filter-select"
                        value={filtros.mes}
                        onChange={(e) => setFiltros(prev => ({...prev, mes: parseInt(e.target.value)}))}
                      >
                        {[
                          {valor: 1, nome: 'Janeiro'}, {valor: 2, nome: 'Fevereiro'}, {valor: 3, nome: 'Março'},
                          {valor: 4, nome: 'Abril'}, {valor: 5, nome: 'Maio'}, {valor: 6, nome: 'Junho'},
                          {valor: 7, nome: 'Julho'}, {valor: 8, nome: 'Agosto'}, {valor: 9, nome: 'Setembro'},
                          {valor: 10, nome: 'Outubro'}, {valor: 11, nome: 'Novembro'}, {valor: 12, nome: 'Dezembro'}
                        ].map(mes => (
                          <option key={mes.valor} value={mes.valor}>{mes.nome}</option>
                        ))}
                      </select>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="card-full summary-stats">
              <div className="stat-item">
                <div className="stat-number">{dadosProcessados.totalOcorrencias}</div>
                <div className="stat-label">Total de Ocorrências</div>
              </div>
              
              <div className="stat-item">
                <div className="stat-number">{dadosProcessados.tempoMedioDeslocamento}</div>
                <div className="stat-label">Tempo Médio Deslocamento</div>
              </div>
              
              <div className="stat-item">
                <div className="stat-number">{dadosProcessados.tempoMedioAtendimento}</div>
                <div className="stat-label">Tempo Médio Atendimento</div>
              </div>

              <div className="stat-item">
                <div className="stat-number">{dadosProcessados.tempoMedioDespachoLiberacao}</div>
                <div className="stat-label">Tempo Despacho à Liberação</div>
              </div>
            </div>

            <div className="dashboard-grid">
              <div className="card ">
                <h2>
                  📊 Resumo das Categorias
                  {filtros.tipoVisualizacao === 'mensal' && (
                    <span style={{ fontSize: '0.8em', color: '#6b7280', fontWeight: 'normal' }}>
                      {' '}({['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
                      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'][filtros.mes - 1]} {filtros.ano})
                    </span>
                  )}
                </h2>
                <div className="chart-container-pie">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={dadosProcessados.dadosCategorizados.resumo}
                        dataKey="quantidade"
                        nameKey="natureza"
                        cx="50%"
                        cy="40%"
                        outerRadius={80}
                        label={({ percentual }) => `${percentual}%`}
                        onClick={(data) => handleCategoriaClick(data)}
                        style={{ cursor: 'pointer' }}
                      >
                        {dadosProcessados.dadosCategorizados.resumo.map((entry, index) => (
                          <Cell 
                            key={index} 
                            fill={entry.color} 
                            style={{ 
                              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                              transition: 'all 0.3s ease'
                            }}
                          />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value, name) => [value, name]} />
                      <Legend 
                        content={<LegendaCustomizada maxItems={10} />}
                        verticalAlign="bottom" 
                        height={80}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="card">
                <h2>🚗 Tempo Médio de Deslocamento</h2>
                <div className="chart-container">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dadosProcessados.dadosDeslocamento}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis 
                        dataKey="categoria" 
                        tick={{ fontSize: 10 }}
                        interval={0}
                        textAnchor="middle"
                        height={60}
                      />
                      <YAxis 
                        tick={{ fontSize: 12 }} 
                        label={{ value: 'Minutos', angle: -90, position: 'insideLeft' }}
                      />
                      <Tooltip 
                        formatter={(value, name, props) => [
                          props.payload.formatted, 
                          'Tempo'
                        ]} 
                      />
                      <Bar dataKey="tempo" fill="#1e40af" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="card">
                <h2>⏰ Tempo Médio de Atendimento</h2>
                <div className="chart-container">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dadosProcessados.dadosAtendimento}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis 
                        dataKey="categoria" 
                        tick={{ fontSize: 10 }}
                        interval={0}
                        textAnchor="middle"
                        height={60}
                      />
                      <YAxis 
                        tick={{ fontSize: 12 }} 
                        label={{ value: 'Minutos', angle: -90, position: 'insideLeft' }}
                      />
                      <Tooltip 
                        formatter={(value, name, props) => [
                          props.payload.formatted, 
                          'Tempo'
                        ]} 
                      />
                      <Bar dataKey="tempo" fill="#16a34a" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="card">
                <h2>❌ Ocorrências Não Atendidas</h2>
                {estatisticasNaoAtendidos ? (
                  <div style={{ padding: '1rem 0' }}>
                    <div className="stat-item" style={{ margin: '0 0 1rem 0' }}>
                      <div className="stat-number" style={{ color: '#dc2626' }}>
                        {estatisticasNaoAtendidos.totalOcorrenciasNaoAtendidas || 0}
                      </div>
                      <div className="stat-label">Total Não Atendidas</div>
                    </div>
                    
                    {estatisticasNaoAtendidos.percentualNaoAtendidas !== undefined && (
                      <div className="stat-item" style={{ margin: '0 0 1rem 0' }}>
                        <div className="stat-number" style={{ color: '#f59e0b', fontSize: '2rem' }}>
                          {estatisticasNaoAtendidos.percentualNaoAtendidas}%
                        </div>
                        <div className="stat-label">Do Total de {estatisticasNaoAtendidos.totalOcorrenciasNoMes}</div>
                      </div>
                    )}
                    
                    {estatisticasNaoAtendidos.motivosNaoAtendimento && (
                      <div style={{ marginTop: '1rem', padding: '1rem', background: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca' }}>
                        <div style={{ fontSize: '0.875rem', color: '#7f1d1d', fontWeight: '600', marginBottom: '0.5rem' }}>
                          Motivos de Não Atendimento:
                        </div>
                        {Object.entries(estatisticasNaoAtendidos.motivosNaoAtendimento).map(([motivo, quantidade], index) => (
                          <div key={index} style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            fontSize: '0.875rem',
                            marginBottom: '0.25rem'
                          }}>
                            <span style={{ color: '#7f1d1d' }}>{motivo}:</span>
                            <span style={{ color: '#dc2626', fontWeight: '600' }}>{quantidade}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {estatisticasNaoAtendidos.tiposSinistroNaoAtendidos && estatisticasNaoAtendidos.tiposSinistroNaoAtendidos.length > 0 && (
                      <div style={{ marginTop: '1rem' }}>
                        <h3 style={{ fontSize: '1rem', color: '#1e293b', marginBottom: '0.75rem' }}>
                          Principais Tipos Não Atendidos:
                          
                        </h3>
                        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                          {estatisticasNaoAtendidos.tiposSinistroNaoAtendidos.slice(0, 5).map((tipo, index) => (
                            <div 
                              key={index} 
                              style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                padding: '0.5rem', 
                                background: index % 2 === 0 ? '#f8fafc' : 'white',
                                borderRadius: '4px',
                                marginBottom: '0.25rem',
                                fontSize: '0.875rem',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                borderLeft: '3px solid transparent'
                              }}
                              onClick={() => handleTipoNaoAtendidoClick(tipo)}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateX(4px)';
                                e.currentTarget.style.boxShadow = '0 4px 12px -2px rgba(59, 130, 246, 0.25)';
                                e.currentTarget.style.background = '#eff6ff';
                                e.currentTarget.style.borderLeftColor = '#3b82f6';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateX(0)';
                                e.currentTarget.style.boxShadow = 'none';
                                e.currentTarget.style.background = index % 2 === 0 ? '#f8fafc' : 'white';
                                e.currentTarget.style.borderLeftColor = 'transparent';
                              }}
                            >
                              <span style={{ 
                                color: '#374151', 
                                fontWeight: '500', 
                                flex: 1, 
                                marginRight: '1rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                              }}>
                                {tipo.natureza?.replace(/\s+/g, ' ').trim() || 'Não informado'}
                                <span style={{ 
                                  fontSize: '0.7rem', 
                                  color: '#9ca3af',
                                  transition: 'color 0.2s ease'
                                }}>
                                  →
                                </span>
                              </span>
                              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                <span style={{ color: '#dc2626', fontWeight: '600' }}>
                                  {tipo.quantidade}
                                </span>
                                <span style={{ color: '#6b7280', fontSize: '0.75rem' }}>
                                  ({tipo.percentual}%)
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '2rem', 
                    color: '#6b7280' 
                  }}>
                    Carregando dados...
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Modal de Detalhes */}
        {categoriaModal && (
          <div className="modal-overlay" onClick={() => setCategoriaModal(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <div className="modal-title">
                  <span className="modal-icon">{categoriaModal.icon}</span>
                  <span>{categoriaModal.titulo}</span>
                </div>
                <button className="modal-close" onClick={() => setCategoriaModal(null)}>
                  ✕
                </button>
              </div>

              <div className="modal-stats">
                <div className="modal-stat-card">
                  <div className="modal-stat-number">{categoriaModal.total}</div>
                  <div className="modal-stat-label">Total de Ocorrências</div>
                </div>
                <div className="modal-stat-card">
                  <div className="modal-stat-number">{categoriaModal.dados.length}</div>
                  <div className="modal-stat-label">Tipos Diferentes</div>
                </div>
                <div className="modal-stat-card">
                  <div className="modal-stat-number">
                    {categoriaModal.dados.length > 0 
                      ? Math.round(categoriaModal.total / categoriaModal.dados.length)
                      : 0}
                  </div>
                  <div className="modal-stat-label">Média por Tipo</div>
                </div>
              </div>

              <div className="modal-chart">
                <ResponsiveContainer width="100%" height={400}>
                  <PieChart>
                    <Pie
                      data={categoriaModal.dados}
                      dataKey="quantidade"
                      nameKey="natureza"
                      cx="50%"
                      cy="50%"
                      outerRadius={120}
                      label={({ percentual }) => `${percentual}%`}
                    >
                      {categoriaModal.dados.map((entry, index) => (
                        <Cell 
                          key={index} 
                          fill={COLORS[index % COLORS.length]}
                          style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }}
                        />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value, name) => [value, name]}
                      contentStyle={{
                        background: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        padding: '12px'
                      }}
                    />
                    <Legend 
                      content={<LegendaCustomizada maxItems={15} />}
                      verticalAlign="bottom"
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="modal-list">
                <div className="modal-list-title">Detalhamento por Natureza</div>
                {categoriaModal.dados
                  .sort((a, b) => b.quantidade - a.quantidade)
                  .map((item, index) => (
                    <div 
                      key={index} 
                      className="modal-list-item"
                      style={{ borderLeftColor: COLORS[index % COLORS.length] }}
                    >
                      <div className="modal-list-item-name">{item.natureza}</div>
                      <div className="modal-list-item-stats">
                        <div className="modal-list-item-count">{item.quantidade} ocorrências</div>
                        <div className="modal-list-item-percent">{item.percentual}%</div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}

      </div>
    </>
  );
}

export default Dashboard;